import { Button, TimePicker, DatePicker } from 'antd';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import colors from '../../constants/colors';
import scale from '../../constants/scale';
import Text from '../Text';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FormikField from '../FormikField';
import FormikRadio from '../FormikRadio';
import storage from '../../utilities/localStorage';
import ProjectFormValues, { ProjectCreation } from '../../types/newProject';
import FormValues from '../../constants/projects';
import SpanRequired from '../Styled/SpanRequired';
import CancelButton from './CancelButton';
import moment from 'moment';
import useRouter from '../../hooks/useRouter';
import Project from '../../types/project';

const quarterlyValidation = Yup.object().shape({
    name: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
    dateStart: Yup.string().required('Required'),
    message: Yup.string().min(2, 'Too Short!').required('Required'),
});

const Container = styled(Form)`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: ${scale.xl};
    grid-template-rows: auto;
    margin-top: ${scale.xl};
`;

const DoubleContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: ${scale.xl};
    grid-template-rows: auto;
`;

const OptionCard = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${colors.white};
    padding: ${scale.lg};
    -webkit-box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
    -moz-box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
    box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
`;

const InputGrid = styled.div`
    display: grid;
    grid-template-rows: auto;
    gap: ${scale.md};
`;

const StyledInputGroup = styled.div`
    display: grid;
    /* grid-template-columns: repeat(2, max-content); */
    grid-template-columns: 32px auto;
    gap: ${scale.md};
    align-items: center;
    margin-top: ${scale.md};
    margin-bottom: ${scale.md};
`;

const StyledDatePicker = styled(DatePicker)`
    width: 100%;
    height: 40px;
    margin-top: ${scale.md};
    display: flex;
    justify-content: center;
`;

const StyledTimePicker = styled(TimePicker)`
    max-width: 280px;
    width: 100%;
    input[type='button'] {
        background-color: ${colors.red} !important;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: ${scale.md};
`;

const StyledButton = styled(Button)`
    width: 194px;
    margin-top: ${scale.md};
`;

const ErrorMessage = styled.div`
    color: ${colors.red};
    font-size: 12px;
`;

const TimePickerContainer = styled.div`
    width: 100%;
`;

const OneTime: React.FC<{
    onClick: () => void;
    onchooseGroup: (project: ProjectCreation) => void;
    projectType: string;
    projectData?: Project;
}> = ({ onClick, onchooseGroup, projectType, projectData }) => {
    const company: any = storage.get('company');
    const router = useRouter();

    const now = moment();
    const messageLength = 320;
    const remainder = 5 - (moment().minute() % 5);
    const roundedTime = now.add(remainder, 'minutes').format('HH:mm a');

    const [defaultTime, setDefaultTime] = useState<moment.Moment>(moment(roundedTime, 'HH:mm a').utc());
    const [defaultDate, setDefaultDate] = useState<moment.Moment>(moment().add(1, 'day'));

    const MessageString = (data: string) => {
        switch (data) {
            case 'One-Time Announcement':
                return 'Write your announcement';
            case 'One-Time Custom Question':
                return 'Write your question';
            case 'One-Time Yes/No Question':
                return 'Write your question';
        }
    };

    const TimePickerString = (data: string) => {
        switch (data) {
            case 'One-Time Announcement':
                return 'Choose a time to send the announcement. The time shown is in your current time zone.';
            default:
                return 'Choose a time to send the temperature check. The time shown is in your current time zone.';
        }
    };

    const CreateProject = (data: ProjectFormValues) => {
        const ProjectToCreate = {
            projectType: projectType,
            name: data.name,
            quarterStartMonth: data.quarterStartMonth,
            dateStart: data.dateStart,
            dateEnd: data.dateStart,
            daysToSample: [],
            sampleTimes: {
                timeStart: data.sampleTimes.timeStart,
                timeEnd: '',
            },
            companyId: company.companyId,
            groups: [],
            message: data.message,
            sendNow: data.sendNow,
        };
        onchooseGroup(ProjectToCreate);
    };

    const FieldValues = {
        ...FormValues,
        dateStart: moment(defaultDate).format(),
    };

    //fill the data we are going to send
    FieldValues.sampleTimes.timeStart = defaultTime?.utc().format();
    FieldValues.sampleTimes.timeEnd = defaultTime?.add(1, 'days').utc().format();

    useEffect(() => {
        //do we have a project?
        //yes
        if (projectData !== undefined) {
            setDefaultTime(moment(projectData.sampleTimes.timeStart));
            setDefaultDate(moment(projectData.dateStart));
            FieldValues.sampleTimes.timeStart = moment(projectData.sampleTimes.timeStart).utc().format();
            FieldValues.name = projectData.name;
            FieldValues.dateStart = projectData.dateStart;
            FieldValues.dateEnd = projectData.dateEnd;
            FieldValues.message =
                projectData.pulses && projectData.pulses.length > 0 ? projectData.pulses[0].question : '';
        }
    }, []);

    return (
        <Formik
            initialValues={FieldValues}
            validationSchema={quarterlyValidation}
            onSubmit={(values) => CreateProject(values)}
        >
            {({ values, setFieldValue, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
                <>
                    {/* {setFieldValue('dateStart', defaultDate)} */}
                    <Container onSubmit={handleSubmit}>
                        <DoubleContainer>
                            <OptionCard>
                                <InputGrid>
                                    <div>
                                        <Text bold>
                                            Name your project <SpanRequired>*</SpanRequired>
                                        </Text>
                                        <FormikField
                                            type="name"
                                            name="name"
                                            placeholder="Project name"
                                            onChange={handleChange}
                                        />
                                        {touched.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                                    </div>
                                    <div>
                                        <Text bold>
                                            Select the date to send <SpanRequired>*</SpanRequired>
                                        </Text>
                                        <StyledDatePicker
                                            format={'MM/DD/YYYY'}
                                            defaultValue={defaultDate}
                                            value={defaultDate}
                                            disabled={
                                                FieldValues.sendNow !== undefined &&
                                                FieldValues.sendNow !== null &&
                                                FieldValues.sendNow === true
                                            }
                                            onChange={(value) => {
                                                setDefaultDate(moment(value));
                                                setFieldValue('dateStart', moment(value).format('MM/DD/YYYY'));
                                                setFieldValue(
                                                    'dateEnd',
                                                    moment(value).add(1, 'days').format('MM/DD/YYYY'),
                                                );
                                            }}
                                            disabledDate={(current) => {
                                                return moment().add(-1, 'days') >= current;
                                            }}
                                        />
                                        {touched.dateStart && <ErrorMessage>{errors.dateStart}</ErrorMessage>}
                                    </div>
                                </InputGrid>
                            </OptionCard>
                            <OptionCard>
                                <InputGrid>
                                    <div>
                                        <Text bold>
                                            {MessageString(projectType)} <SpanRequired>*</SpanRequired>
                                        </Text>
                                        <FormikField
                                            type="message"
                                            name="message"
                                            as="textarea"
                                            defaultValue={values.message}
                                            maxLength={messageLength}
                                            style={{ minHeight: 250 }}
                                            onChange={handleChange}
                                        />
                                        {`${values.message.length}/${messageLength}`}
                                        {touched.message && <ErrorMessage>{errors.message}</ErrorMessage>}
                                    </div>
                                </InputGrid>
                            </OptionCard>
                        </DoubleContainer>
                        <OptionCard>
                            <InputGrid>
                                <Text bold>
                                    {TimePickerString(projectType)}
                                    <SpanRequired>*</SpanRequired>
                                </Text>
                                <StyledInputGroup>
                                    <FormikRadio
                                        name="sendNow"
                                        value={false}
                                        onChange={() => {
                                            setFieldValue('sendNow', false);
                                            FieldValues.sendNow = false;
                                        }}
                                    />
                                    <TimePickerContainer>
                                        <StyledTimePicker
                                            disabled={
                                                FieldValues.sendNow !== undefined &&
                                                FieldValues.sendNow !== null &&
                                                FieldValues.sendNow === true
                                            }
                                            defaultValue={defaultTime.local()}
                                            value={defaultTime.local()}
                                            onChange={(value) => {
                                                setDefaultTime(moment(value).utc());
                                                FieldValues.sampleTimes.timeStart = value?.utc().format();
                                            }}
                                            onSelect={(value) => {
                                                setDefaultTime(moment(value).utc());
                                                FieldValues.sampleTimes.timeStart = value?.utc().format();
                                            }}
                                            use12Hours
                                            format="hh:mm a"
                                            showNow={false}
                                            minuteStep={5}
                                        />
                                    </TimePickerContainer>
                                    {touched.sampleTimes && <ErrorMessage>{errors.sampleTimes}</ErrorMessage>}
                                    <FormikRadio
                                        name="sendNow"
                                        value={true}
                                        onChange={() => {
                                            setFieldValue('sendNow', true);
                                            FieldValues.sendNow = true;
                                        }}
                                    />
                                    <Text size="14">Send immediately</Text>
                                </StyledInputGroup>
                                {process.env.NODE_ENV === 'development' ? (
                                    <>
                                        <pre>{JSON.stringify(values, null, 2)}</pre>
                                    </>
                                ) : null}
                            </InputGrid>
                        </OptionCard>
                    </Container>
                    <ButtonGroup>
                        <CancelButton
                            disabled={isSubmitting}
                            onClick={() => onClick()}
                            size="large"
                            style={{ color: colors.black }}
                            htmlType="submit"
                        >
                            X Cancel
                        </CancelButton>
                        <StyledButton
                            disabled={isSubmitting}
                            onClick={() => handleSubmit()}
                            size="large"
                            style={{ color: colors.white, backgroundColor: colors.red }}
                            htmlType="submit"
                        >
                            {router.location.pathname === '/editproject' ? 'Update Project' : 'Choose Groups'}
                        </StyledButton>
                    </ButtonGroup>
                </>
            )}
        </Formik>
    );
};

export default OneTime;
